import { inject, Injectable, NgZone, signal } from "@angular/core";
import { environment } from "../../../environments/environment";

/** Une requête vers le serveur qui attend depuis plus longtemps : « réseau lent ». */
export const NETWORK_SLOW_MS = 8_000;
/** Délai maximal d'une sonde (`GET /api/ping`) : au-delà, le serveur est réputé injoignable. */
export const NETWORK_PROBE_TIMEOUT_MS = 5_000;
/**
 * Espacement des sondes tant que le réseau est coupé (puis 15 s, indéfiniment) : un wifi qui « tient »
 * sans rien laisser passer ne déclenche pas l'événement `online` — c'est la sonde qui voit le retour.
 * Une sonde est minuscule (`GET /api/ping`) : au plus 15 s d'écran périmé après le retour.
 */
export const NETWORK_PROBE_STEPS_MS = [2_000, 4_000, 8_000, 15_000];
/** « Réseau revenu » reste affiché ce temps-là. */
export const NETWORK_BACK_MS = 4_000;
/**
 * Lecture de FOND (cloche, « Mes tâches », mode atelier) : abandonnée au-delà. Sur un wifi qui
 * « tient » sans rien laisser passer, une requête peut rester pendante plusieurs minutes — et une
 * lecture en cours est RÉUTILISÉE par les suivantes : l'écran restait figé sans rien dire.
 */
export const BACKGROUND_READ_TIMEOUT_MS = 20_000;

/**
 * ÉTAT DU RÉSEAU — vu du SERVEUR, pas seulement de la carte wifi (01/10/2026).
 *
 * Un PC d'atelier a souvent un wifi « connecté » qui ne mène nulle part : `navigator.onLine`
 * ne suffit pas. Sources :
 *  - les événements `online` / `offline` du navigateur (`offline` est fiable : plus d'interface) ;
 *  - chaque requête vers NOTRE serveur (`NetworkStatusInterceptor`) : une réponse, quel qu'en soit
 *    le code, prouve qu'il est joignable ; un échec SANS réponse (statut 0) le rend suspect ;
 *  - la socket générale (`GeneralSocketService`) : déconnectée = suspect, connectée = joignable.
 *
 * Un soupçon n'affiche rien : une sonde (`GET /api/ping`, sans cache, 5 s au plus) le confirme.
 * Hors ligne, la sonde revient de plus en plus espacée (2 s → 15 s) ; « Réessayer » la relance.
 * Au retour, `reconnects` s'incrémente : les écrans qui lisent en fond (cloche, « Mes tâches »,
 * mode atelier) s'y branchent pour UNE relecture, au lieu d'attendre leur minuterie.
 *
 * Décision du 01/10/2026 : pas d'envoi différé des saisies — on DIT qu'il n'y a plus de réseau
 * (`vl-network-status`) et qu'une saisie n'a pas été enregistrée (`ErrorInterceptor`, statut 0).
 */
@Injectable({ providedIn: "root" })
export class NetworkStatusService {
  private zone = inject(NgZone);

  /** Le serveur est joignable. */
  readonly online = signal(typeof navigator === "undefined" || navigator.onLine !== false);
  /** Depuis quand il ne l'est plus (ms), `null` en ligne. */
  readonly offlineSince = signal<number | null>(this.online() ? null : Date.now());
  /** Dernière réponse reçue du serveur (ms) : « informations de 10:42 ». */
  readonly lastContact = signal<number | null>(null);
  /** Une requête attend depuis plus de `NETWORK_SLOW_MS`. */
  readonly slow = signal(false);
  /** Le réseau vient de revenir (« Réseau revenu », `NETWORK_BACK_MS`). */
  readonly justBack = signal(false);
  /** Une sonde est en cours. */
  readonly probing = signal(false);
  /** Pourquoi hors ligne : `network` (aucune réponse) ou `server` (le relais répond, pas le serveur : 502 / 504). */
  readonly reason = signal<"network" | "server">("network");
  /** Incrémenté à chaque RETOUR du réseau — une relecture par écran, pas une rafale. */
  readonly reconnects = signal(0);

  private pending = new Map<number, number>();
  private seq = 0;
  private slowTimer: ReturnType<typeof setInterval> | null = null;
  private probeTimer: ReturnType<typeof setTimeout> | null = null;
  private backTimer: ReturnType<typeof setTimeout> | null = null;
  private probeStep = 0;
  private probeInFlight: Promise<boolean> | null = null;

  constructor() {
    if (typeof window === "undefined") return;
    this.zone.runOutsideAngular(() => {
      window.addEventListener("offline", () => this.zone.run(() => this.goOffline()));
      // `online` ne prouve rien (wifi sans accès) : on vérifie.
      window.addEventListener("online", () => void this.probeNow());
    });
    if (!this.online()) this.scheduleProbe();
  }

  /** Le serveur a répondu (n'importe quel code HTTP, ou la socket s'est connectée). */
  reportContact(): void {
    this.lastContact.set(Date.now());
    if (!this.online()) this.zone.run(() => this.goOnline());
  }

  /** Une requête vers le serveur a échoué sans réponse (statut 0, ou 502 / 504 du relais), ou la socket est tombée. */
  reportFailure(): void {
    if (!this.online()) return;
    void this.probeNow();
  }

  /** Suivi d'une requête vers le serveur : rend la fonction à appeler à sa fin. */
  trackRequest(): () => void {
    const id = ++this.seq;
    this.pending.set(id, Date.now());
    this.ensureSlowWatch();
    return () => {
      this.pending.delete(id);
      if (!this.pending.size) this.setSlow(false);
    };
  }

  /** « Réessayer » : une sonde tout de suite (rend `true` si le serveur répond). */
  probeNow(): Promise<boolean> {
    if (this.probeInFlight) return this.probeInFlight;
    if (this.probeTimer) clearTimeout(this.probeTimer);
    this.probeTimer = null;
    this.probing.set(true);
    this.probeInFlight = this.ping().then((ok) => {
      // Libérée AVANT de conclure : hors ligne, `goOffline` programme la sonde suivante — elle ne
      // l'était jamais tant que celle-ci passait pour « en cours » (vu au TU du 01/10/2026).
      this.probeInFlight = null;
      this.probing.set(false);
      this.zone.run(() => (ok ? this.goOnline() : this.goOffline()));
      return ok;
    });
    return this.probeInFlight;
  }

  // ── interne ────────────────────────────────────────────────────────────────

  private async ping(): Promise<boolean> {
    if (typeof fetch === "undefined") return true;
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = setTimeout(() => ctrl?.abort(), NETWORK_PROBE_TIMEOUT_MS);
    try {
      // Sans cache, sans intercepteur (ni jeton, ni barre de chargement) : une réponse, c'est tout.
      const res = await fetch(`${environment.api()}/ping?t=${Date.now()}`, { cache: "no-store", signal: ctrl?.signal });
      const ok = res.status > 0 && res.status < 500;
      if (ok) this.lastContact.set(Date.now());
      else this.reason.set("server");
      return ok;
    } catch {
      this.reason.set("network");
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  private goOffline(): void {
    if (this.online()) {
      this.online.set(false);
      this.offlineSince.set(Date.now());
      this.justBack.set(false);
      this.probeStep = 0;
    }
    this.scheduleProbe();
  }

  private goOnline(): void {
    if (this.probeTimer) clearTimeout(this.probeTimer);
    this.probeTimer = null;
    this.probeStep = 0;
    if (this.online()) return;
    this.online.set(true);
    this.offlineSince.set(null);
    this.reconnects.update((n) => n + 1);
    this.justBack.set(true);
    if (this.backTimer) clearTimeout(this.backTimer);
    this.backTimer = setTimeout(() => this.zone.run(() => this.justBack.set(false)), NETWORK_BACK_MS);
  }

  private scheduleProbe(): void {
    if (this.probeTimer || this.probeInFlight) return;
    const delay = NETWORK_PROBE_STEPS_MS[Math.min(this.probeStep, NETWORK_PROBE_STEPS_MS.length - 1)];
    this.probeStep++;
    this.zone.runOutsideAngular(() => {
      this.probeTimer = setTimeout(() => {
        this.probeTimer = null;
        void this.probeNow();
      }, delay);
    });
  }

  /** Minuterie HORS d'Angular (pas de détection de changements toutes les secondes) ; arrêtée sans requête en cours. */
  private ensureSlowWatch(): void {
    if (this.slowTimer) return;
    this.zone.runOutsideAngular(() => {
      this.slowTimer = setInterval(() => {
        if (!this.pending.size) {
          clearInterval(this.slowTimer!);
          this.slowTimer = null;
          this.setSlow(false);
          return;
        }
        const now = Date.now();
        let oldest = now;
        for (const t of this.pending.values()) oldest = Math.min(oldest, t);
        this.setSlow(now - oldest >= NETWORK_SLOW_MS);
      }, 1_000);
    });
  }

  private setSlow(v: boolean): void {
    if (this.slow() !== v) this.zone.run(() => this.slow.set(v));
  }
}
